import io from "socket.io-client";
import api from "../services/api";

const getSessionToken = () => {
  const authorizationHeader = String(api?.defaults?.headers?.Authorization || "").trim();
  if (authorizationHeader) {
    return authorizationHeader;
  }

  const storedToken = String(
    typeof window !== "undefined" ? window.localStorage?.getItem("token") || "" : ""
  ).trim();
  if (!storedToken) {
    return "";
  }

  try {
    const parsedToken = JSON.parse(storedToken);
    return parsedToken ? `Bearer ${parsedToken}` : "";
  } catch (_error) {
    return storedToken.startsWith("Bearer ") ? storedToken : `Bearer ${storedToken}`;
  }
};

class SocketWorker {
  constructor(companyId, userId) {
    const nextContext = {
      companyId: String(companyId || "").trim(),
      userId: String(userId || "").trim(),
      token: getSessionToken()
    };

    if (SocketWorker.instance) {
      SocketWorker.instance.refreshContext(nextContext);
      return SocketWorker.instance;
    }

    this.companyId = nextContext.companyId;
    this.userId = nextContext.userId;
    this.token = nextContext.token;
    this.socket = null;
    this.eventListeners = {};
    this.configureSocket();
    this._visibilityHandler = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        this.connect();
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this._visibilityHandler);
    }
    SocketWorker.instance = this;
    return this;
  }

  hasContextChanged(nextContext) {
    return (
      String(this.companyId || "") !== String(nextContext.companyId || "") ||
      String(this.userId || "") !== String(nextContext.userId || "") ||
      String(this.token || "") !== String(nextContext.token || "")
    );
  }

  refreshContext(nextContext) {
    if (!this.hasContextChanged(nextContext) && this.socket) {
      return;
    }

    this.companyId = String(nextContext.companyId || "").trim();
    this.userId = String(nextContext.userId || "").trim();
    this.token = String(nextContext.token || "").trim();

    this.teardownSocket();
    this.configureSocket();
    this.rebindListeners();
  }

  configureSocket() {
    if (!this.companyId || !process.env.REACT_APP_BACKEND_URL) {
      this.socket = null;
      return;
    }

    this.socket = io(`${process.env.REACT_APP_BACKEND_URL}/${this.companyId}`, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      transports: ["websocket", "polling"],
      pingTimeout: 5000,
      query: {
        userId: this.userId,
        token: this.token
      }
    });

    this.socket.on("connect", () => {
      console.log("Conectado ao servidor Socket.IO");
    });

    this.socket.on("disconnect", (reason) => {
      console.log("Desconectado do servidor Socket.IO", reason);
    });
  }

  rebindListeners() {
    if (!this.socket) {
      return;
    }

    Object.entries(this.eventListeners).forEach(([event, callbacks]) => {
      callbacks.forEach((callback) => {
        this.socket.on(event, callback);
      });
    });
  }

  teardownSocket() {
    if (!this.socket) {
      return;
    }

    Object.entries(this.eventListeners).forEach(([event, callbacks]) => {
      callbacks.forEach((callback) => {
        this.socket.off(event, callback);
      });
    });

    this.socket.disconnect();
    this.socket = null;
  }

  on(event, callback) {
    if (!event || typeof callback !== "function") {
      return;
    }

    if (!Array.isArray(this.eventListeners[event])) {
      this.eventListeners[event] = [];
    }

    if (!this.eventListeners[event].includes(callback)) {
      this.eventListeners[event].push(callback);
    }

    this.connect();
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  emit(event, data) {
    this.connect();
    if (this.socket) {
      this.socket.emit(event, data);
    }
  }

  off(event, callback) {
    if (!this.eventListeners[event]) {
      return;
    }

    this.connect();

    if (callback) {
      if (this.socket) {
        this.socket.off(event, callback);
      }
      this.eventListeners[event] = this.eventListeners[event].filter(
        (registeredCallback) => registeredCallback !== callback
      );
      if (!this.eventListeners[event].length) {
        delete this.eventListeners[event];
      }
      return;
    }

    if (this.socket) {
      this.eventListeners[event].forEach((registeredCallback) => {
        this.socket.off(event, registeredCallback);
      });
    }
    delete this.eventListeners[event];
  }

  disconnect() {
    if (this._visibilityHandler && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this._visibilityHandler);
      this._visibilityHandler = null;
    }
    this.teardownSocket();
    this.eventListeners = {};
    SocketWorker.instance = null;
    console.log("Socket desconectado manualmente");
  }

  connect() {
    if (!this.socket) {
      this.configureSocket();
      this.rebindListeners();
      return;
    }

    if (this.socket.disconnected && typeof this.socket.connect === "function") {
      this.socket.connect();
    }
  }

  forceReconnect() {
    this.refreshContext({
      companyId: this.companyId,
      userId: this.userId,
      token: getSessionToken()
    });
  }
}

const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
